import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import DealForgeLogo from "../components/DealForgeLogo";
import {
  FileSignature,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Eraser,
  Download,
  UserPlus,
  LogIn,
  Network,
  Zap
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SignContractPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [signatureRequest, setSignatureRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [inviteToken, setInviteToken] = useState(null);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    fetchSignatureRequest();
  }, [token]);

  useEffect(() => {
    if (signatureRequest && canvasRef.current) {
      initCanvas();
    }
  }, [signatureRequest]);

  const fetchSignatureRequest = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/sign/${token}`);
      setSignatureRequest(response.data);
    } catch (error) {
      setError(error.response?.data?.detail || "Failed to load signature request");
    } finally {
      setLoading(false);
    }
  };

  const initCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    const ctx = canvas.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    initCanvas();
    setHasSignature(false);
  };

  const handleSign = async () => {
    if (!agreed) {
      toast.error("You must agree to the terms to sign");
      return;
    }
    
    if (!hasSignature) {
      toast.error("Please draw your signature");
      return;
    }

    setSigning(true);
    try {
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL("image/png");
      
      const response = await axios.post(`${BACKEND_URL}/api/sign/${token}`, {
        signature_data: signatureData,
        agreed_to_terms: true
      });
      
      setSigned(true);
      setInviteToken(response.data.invite_token);
      toast.success("Contract signed successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sign contract");
    } finally {
      setSigning(false);
    }
  };

  const handleCreateAccount = async () => {
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setCreatingAccount(true);
    try {
      await axios.post(`${BACKEND_URL}/api/signer/create-account`, {
        invite_token: inviteToken,
        password: password
      });
      
      setAccountCreated(true);
      toast.success("Account created! You can now log in.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create account");
    } finally {
      setCreatingAccount(false);
    }
  };

  const handleDecline = async () => {
    if (!window.confirm("Are you sure you want to decline signing this contract?")) {
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/sign/${token}/decline`);
      setDeclined(true);
      toast.success("Signature declined");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to decline");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white/70">Loading signature request...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-red-500/10 border border-red-500/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Unable to Load</h1>
          <p className="text-white/70">{error}</p>
        </div>
      </div>
    );
  }

  if (signed) {
    const referralCode = signatureRequest?.sender_referral_code;
    const senderName = signatureRequest?.sender_name || "A DealForge member";
    const joinLink = referralCode
      ? `/join?ref=${referralCode}`
      : `/join`;

    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          {/* Success Message */}
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-emerald-500/10 border border-emerald-500/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-3">Contract Signed!</h1>
            <p className="text-white/60 text-sm">
              Thank you, {signatureRequest?.signer_name}. Your signature has been recorded.
            </p>
          </div>

          {/* Network Credits referral CTA */}
          {referralCode && !accountCreated && (
            <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-xl p-5 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Network className="w-4 h-4 text-[#D4AF37]" />
                <p className="text-[10px] text-[#D4AF37] uppercase tracking-widest font-semibold">DealForge Network</p>
              </div>
              <p className="text-white text-sm font-semibold mb-1">
                You were invited by {senderName}
              </p>
              <p className="text-white/50 text-xs mb-4 leading-relaxed">
                Join DealForge and {senderName} earns Network Credits when you become an active member.
                Serious operators don't just use the network — they build it.
              </p>
              <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-[#D4AF37]/[0.06] border border-[#D4AF37]/20 rounded-lg">
                <Zap className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span className="text-[#D4AF37] text-xs font-mono font-bold">{referralCode}</span>
                <span className="text-white/30 text-xs ml-auto">Referral code applied automatically</span>
              </div>
              {!showCreateAccount && (
                <Button
                  onClick={() => setShowCreateAccount(true)}
                  className="w-full bg-[#D4AF37] hover:bg-[#C4A030] text-black font-bold"
                  data-testid="show-create-account-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Join DealForge Network
                </Button>
              )}
            </div>
          )}

          {/* Generic account creation (no referral code) */}
          {!referralCode && !accountCreated && inviteToken && !showCreateAccount && (
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6 rounded-2xl mb-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
                  <UserPlus className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Create a DealForge Account</h3>
                  <p className="text-white/50 text-sm">Track your contracts and collaborate on deals</p>
                </div>
              </div>
              <Button
                onClick={() => setShowCreateAccount(true)}
                className="w-full gold-gradient-btn rounded-none font-semibold"
                data-testid="show-create-account-btn"
              >
                Create Free Account
              </Button>
              <p className="text-white/40 text-xs text-center mt-3">
                Or close this window if you're done
              </p>
            </div>
          )}

          {/* Account Creation Form */}
          {showCreateAccount && !accountCreated && (
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6 rounded-2xl mb-4">
              <div className="flex items-center gap-2 mb-4">
                <DealForgeLogo size="lg" />
              </div>
              {referralCode && (
                <div className="flex items-center gap-2 px-3 py-2 bg-[#D4AF37]/[0.06] border border-[#D4AF37]/20 rounded-lg mb-4">
                  <Network className="w-3 h-3 text-[#D4AF37]" />
                  <span className="text-[#D4AF37] text-xs">Referred by {senderName} · Code {referralCode} applied</span>
                </div>
              )}
              <h3 className="text-white font-semibold mb-4">Create Your Account</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-white/50 text-sm block mb-1">Email</label>
                  <Input
                    type="email"
                    value={signatureRequest?.signer_email || ""}
                    disabled
                    className="bg-black border-white/[0.08] text-white/50"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-sm block mb-1">Password</label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="bg-black border-white/[0.08] text-white"
                    data-testid="create-account-password"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-sm block mb-1">Confirm Password</label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="bg-black border-white/[0.08] text-white"
                    data-testid="create-account-confirm-password"
                  />
                </div>
                <Button
                  onClick={handleCreateAccount}
                  disabled={creatingAccount || !password || !confirmPassword}
                  className="w-full bg-[#D4AF37] hover:bg-[#C4A030] text-black font-bold"
                  data-testid="create-account-submit-btn"
                >
                  {creatingAccount ? "Creating..." : "Create Account & Join Network"}
                </Button>
                <button
                  onClick={() => setShowCreateAccount(false)}
                  className="w-full text-white/40 text-sm hover:text-white/50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Account Created Success */}
          {accountCreated && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 p-6 rounded-2xl mb-4">
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle className="w-6 h-6 text-emerald-500" />
                <h3 className="text-[#00FF88] font-semibold">Account Created!</h3>
              </div>
              {referralCode && (
                <p className="text-white/50 text-xs mb-3 leading-relaxed">
                  Your account has been linked to {senderName}'s referral. They'll earn DealForge Network Credits once you become an active member.
                </p>
              )}
              <p className="text-white/70 text-sm mb-4">
                You can now log in to DealForge with your email and password.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-none font-semibold"
                data-testid="go-to-login-btn"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Go to Login
              </Button>
            </div>
          )}

          {/* Close message */}
          {!showCreateAccount && !accountCreated && !referralCode && (
            <p className="text-white/40 text-sm text-center">
              You can close this window now.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (declined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-white/20/10 border border-white/[0.15]/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-white/70" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Signature Declined</h1>
          <p className="text-white/70 mb-6">
            You have declined to sign this contract. The sender will be notified.
          </p>
          <p className="text-white/70 text-sm">
            You can close this window now.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border-b border-white/[0.08] py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileSignature className="w-6 h-6 text-[#D4AF37]" />
            <div>
              <h1 className="text-white font-bold">E-Signature Request</h1>
              <p className="text-white/40 text-xs">Powered by <span className="text-[#D4AF37]">DEAL</span><span className="text-[#C4A030]">FORGE</span></p>
            </div>
          </div>
          <DealForgeLogo size="sm" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        {/* Signer Info */}
        <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6 mb-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-white/70 text-sm uppercase">Signing As</p>
              <p className="text-white font-medium">{signatureRequest?.signer_name}</p>
              <p className="text-white/70 text-sm capitalize">{signatureRequest?.signer_role}</p>
            </div>
            <div>
              <p className="text-white/70 text-sm uppercase">Document</p>
              <p className="text-white font-medium">{signatureRequest?.contract_name}</p>
            </div>
          </div>
        </div>

        {/* Contract Content */}
        <div className="bg-white text-black p-8 mb-6 max-h-96 overflow-y-auto">
          <div 
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(signatureRequest?.contract_content || '') }}
          />
        </div>

        {/* Signature Section */}
        <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
          <h2 className="text-lg font-bold text-white mb-4">Your Signature</h2>
          
          <div className="bg-white rounded mb-4 relative">
            <canvas
              ref={canvasRef}
              width={600}
              height={200}
              className="w-full cursor-crosshair touch-none"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
            <div className="absolute bottom-4 left-4 right-4 border-b-2 border-dashed border-white/30" />
            <p className="absolute bottom-1 left-4 text-white/70 text-xs">
              Sign above the line
            </p>
          </div>
          
          <div className="flex justify-between items-center mb-6">
            <Button
              variant="outline"
              onClick={clearSignature}
              className="border-white/[0.1] text-white/70 hover:bg-white/[0.06] rounded-none"
            >
              <Eraser className="w-4 h-4 mr-2" />
              Clear
            </Button>
            <p className="text-white/70 text-sm">
              Draw your signature using mouse or touch
            </p>
          </div>

          {/* Agreement Checkbox */}
          <div className="flex items-start gap-3 p-4 bg-black border border-white/[0.08] mb-6">
            <Checkbox 
              id="agree" 
              checked={agreed}
              onCheckedChange={(checked) => setAgreed(checked)}
              className="mt-1"
            />
            <label htmlFor="agree" className="text-white/70 text-sm cursor-pointer">
              I agree that my electronic signature is the legal equivalent of my manual signature. 
              I have read and agree to the terms of this contract.
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={handleDecline}
              className="border-red-500/50 text-[#FF3B30] hover:bg-red-500/10 rounded-none"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Decline to Sign
            </Button>
            <Button
              onClick={handleSign}
              disabled={!agreed || !hasSignature || signing}
              className="gold-gradient-btn rounded-none font-bold"
            >
              {signing ? "Signing..." : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Sign Contract
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
